import { Component } from '@theme/component';

/**
 * Snug Whiskey Fest — Ticket Purchase Form Component
 *
 * Manages:
 * - Quantity stepper (plus/minus buttons)
 * - Dynamic guest fields based on quantity
 * - Full form validation (required fields + 3 consent checkboxes)
 * - Dynamic price total on Add to Cart button
 * - Dark-themed venue map
 */
class SwfTicketFormComponent extends Component {
  #maxTickets = 6;
  #unitPrice = 0;
  static #STORAGE_KEY = 'swf_buyer_details';
  static #BUYER_FIELDS = ['buyer_name', 'buyer_email', 'buyer_phone', 'buyer_address'];

  connectedCallback() {
    super.connectedCallback();
    this.#maxTickets = parseInt(this.dataset.maxTickets) || 6;
    this.#unitPrice = parseInt(this.dataset.unitPrice) || 0;
    this.#restoreBuyerDetails();
    this.#initVenueMap();
    this.#updateStepperState();
    this.#syncOrderPricing();
    this.#updateAddToCartState();

    // Intercept form submit to enforce guest-field validation server-side can't do
    const form = this.querySelector('#swf-ticket-purchase');
    if (form) {
      form.addEventListener('submit', (e) => this.#handleFormSubmit(e));
    }

    // Auto-open form if ?buy is in the URL — but only once per session per product
    if (new URLSearchParams(window.location.search).has('buy')) {
      const productKey = `swf_autoopened_${window.location.pathname}`;
      if (!sessionStorage.getItem(productKey)) {
        sessionStorage.setItem(productKey, '1');
        requestAnimationFrame(() => this.handleToggleForm());
      }
    }

    // Close form when clicking backdrop
    const backdrop = this.refs.formBackdrop;
    if (backdrop) {
      backdrop.addEventListener('click', () => this._closeForm());
    }

    // Hide sticky CTA when footer is visible
    this.#initFooterObserver();

    // Curve the VIP card heading into an arched banner
    this.#renderArchedVipHeading();

    // Make the VIP card's last paragraph (e.g. "Limited to 100 VIP guests") open the form
    this.#wireVipCalloutClick();
  }

  #wireVipCalloutClick() {
    const vipBox = this.querySelector('.swf-description div[style*="background: #0f3d36"], .swf-description div[style*="background:#0f3d36"], .swf-description div[style*="background: #004c45"], .swf-description div[style*="background:#004c45"]');
    if (!vipBox) return;
    const paras = vipBox.querySelectorAll(':scope > p');
    const lastP = paras[paras.length - 1];
    if (!lastP || lastP.dataset.swfClickable === '1') return;

    lastP.dataset.swfClickable = '1';
    lastP.setAttribute('role', 'button');
    lastP.setAttribute('tabindex', '0');
    lastP.setAttribute('aria-label', 'Get your tickets');
    lastP.addEventListener('click', () => this.handleToggleForm());
    lastP.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleToggleForm();
      }
    });
  }

  #renderArchedVipHeading() {
    const vipBox = this.querySelector('.swf-description div[style*="background: #0f3d36"], .swf-description div[style*="background:#0f3d36"], .swf-description div[style*="background: #004c45"], .swf-description div[style*="background:#004c45"]');
    if (!vipBox) return;
    const h3 = vipBox.querySelector('h3');
    if (!h3 || h3.dataset.swfArched === '1') return;

    const text = h3.textContent.trim();
    if (!text) return;

    const id = `swf-vip-arc-${Math.random().toString(36).slice(2, 8)}`;
    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('viewBox', '0 0 600 140');
    svg.setAttribute('class', 'swf-vip-arch-svg');
    svg.setAttribute('aria-label', text);
    svg.setAttribute('role', 'img');

    const defs = document.createElementNS(svgNS, 'defs');
    const path = document.createElementNS(svgNS, 'path');
    path.setAttribute('id', id);
    path.setAttribute('d', 'M 40 120 Q 300 0 560 120');
    path.setAttribute('fill', 'none');
    defs.appendChild(path);
    svg.appendChild(defs);

    const textEl = document.createElementNS(svgNS, 'text');
    textEl.setAttribute('class', 'swf-vip-arch-text');
    const textPath = document.createElementNS(svgNS, 'textPath');
    textPath.setAttributeNS('http://www.w3.org/1999/xlink', 'href', `#${id}`);
    textPath.setAttribute('href', `#${id}`);
    textPath.setAttribute('startOffset', '50%');
    textPath.setAttribute('text-anchor', 'middle');
    textPath.textContent = text;
    textEl.appendChild(textPath);
    svg.appendChild(textEl);

    h3.dataset.swfArched = '1';
    h3.textContent = '';
    h3.appendChild(svg);
  }

  #initFooterObserver() {
    const ctaWrap = this.refs.ctaWrap;
    const footer = document.querySelector('footer, .footer, #shopify-section-footer');
    if (!ctaWrap || !footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        ctaWrap.style.opacity = entry.isIntersecting ? '0' : '1';
        ctaWrap.style.pointerEvents = entry.isIntersecting ? 'none' : 'auto';
      },
      { threshold: 0.1 }
    );
    observer.observe(footer);
  }

  /**
   * Initialise the dark-themed Leaflet map for the venue card.
   */
  #initVenueMap() {
    const mapEl = document.getElementById('swf-venue-map');
    if (!mapEl || typeof L === 'undefined') return;

    const lat = parseFloat(mapEl.dataset.lat);
    const lng = parseFloat(mapEl.dataset.lng);
    const venueName = mapEl.dataset.venue || '';
    if (isNaN(lat) || isNaN(lng)) return;

    const map = L.map(mapEl, {
      scrollWheelZoom: false,
      zoomControl: true,
    }).setView([lat, lng], 16);

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
    }).addTo(map);

    const icon = L.divIcon({
      className: 'swf-map-pin',
      html: '<svg width="28" height="36" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 22 14 22s14-11.5 14-22C28 6.268 21.732 0 14 0z" fill="#c04d00"/><circle cx="14" cy="14" r="6" fill="#fff"/></svg>',
      iconSize: [28, 36],
      iconAnchor: [14, 36],
      popupAnchor: [0, -36],
    });

    L.marker([lat, lng], { icon }).addTo(map).bindPopup(venueName).openPopup();
  }

  // ── Toggle ticket form ──

  handleToggleForm() {
    const panel = this.refs.formPanel;
    const cta = this.refs.ticketsCta;
    const backdrop = this.refs.formBackdrop;
    if (!panel || !cta) return;

    const isHidden = panel.hidden;
    if (isHidden) {
      panel.hidden = false;
      requestAnimationFrame(() => {
        panel.classList.add('is-visible');
        if (backdrop) backdrop.classList.add('is-visible');
      });
      cta.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    } else {
      this._closeForm();
    }
  }

  _closeForm() {
    const panel = this.refs.formPanel;
    const cta = this.refs.ticketsCta;
    const backdrop = this.refs.formBackdrop;
    if (!panel) return;

    panel.classList.remove('is-visible');
    if (backdrop) backdrop.classList.remove('is-visible');
    if (cta) cta.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    panel.addEventListener('transitionend', () => {
      if (!panel.classList.contains('is-visible')) {
        panel.hidden = true;
      }
    }, { once: true });
  }

  // ── Quantity stepper ──

  handleQtyMinus() {
    const input = this.refs.quantity;
    if (!input) return;
    const val = parseInt(input.value) || 1;
    if (val > 1) {
      input.value = val - 1;
      this.#onQuantityChanged();
    }
  }

  handleQtyPlus() {
    const input = this.refs.quantity;
    if (!input) return;
    const val = parseInt(input.value) || 1;
    if (val < this.#maxTickets) {
      input.value = val + 1;
      this.#onQuantityChanged();
    }
  }

  handleQuantityChange() {
    this.#onQuantityChanged();
  }

  #onQuantityChanged() {
    const qty = this.#getQuantity();
    this.#showGuestFields(qty);
    this.#updateStepperState();
    this.#syncOrderPricing();
    this.#updateAddToCartState();
  }

  #getQuantity() {
    return parseInt(this.refs.quantity?.value) || 1;
  }

  #updateStepperState() {
    const qty = this.#getQuantity();
    if (this.refs.qtyMinus) this.refs.qtyMinus.disabled = qty <= 1;
    if (this.refs.qtyPlus) this.refs.qtyPlus.disabled = qty >= this.#maxTickets;
  }

  // ── Guest fields ──

  #showGuestFields(qty) {
    const container = this.refs.guestContainer;
    if (!container) return;

    for (const fieldset of container.querySelectorAll('.swf-guest')) {
      const idx = parseInt(fieldset.dataset.guestIndex);
      const visible = idx <= qty;
      fieldset.hidden = !visible;

      const nameInput = fieldset.querySelector(`[name="properties[_guest_${idx}_name]"]`);
      const emailInput = fieldset.querySelector(`[name="properties[_guest_${idx}_email]"]`);
      if (nameInput) nameInput.required = visible;
      if (emailInput) emailInput.required = visible;

      if (!visible) {
        for (const input of fieldset.querySelectorAll('input')) {
          input.value = '';
        }
      }
    }
  }

  // ── Form validation ──

  handleConsentChange() {
    this.#updateAddToCartState();
  }

  handleFieldInput() {
    this.#saveBuyerDetails();
    this.#updateAddToCartState();
  }

  scrollToDetails() {
    const target = this.refs.buyerDetails;
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const firstInput = target.querySelector('input, select, textarea');
    if (firstInput) {
      setTimeout(() => firstInput.focus({ preventScroll: true }), 400);
    }
  }

  #updateAddToCartState() {
    const btn = this.refs.addToCartBtn;
    if (!btn) return;

    const allConsent =
      this.refs.ageCheckbox?.checked &&
      this.refs.commsCheckbox?.checked &&
      this.refs.termsCheckbox?.checked;

    if (!allConsent) {
      btn.disabled = true;
      return;
    }

    // Check all visible required fields have values
    const form = this.querySelector('#swf-ticket-purchase');
    if (!form) {
      btn.disabled = true;
      return;
    }

    let allFilled = true;
    for (const input of form.querySelectorAll('input[required]')) {
      // Skip hidden guest fields
      const fieldset = input.closest('.swf-guest');
      if (fieldset && fieldset.hidden) continue;

      // Skip checkbox inputs (handled above)
      if (input.type === 'checkbox') continue;

      if (!input.value.trim()) {
        allFilled = false;
        break;
      }

      // Basic email format check
      if (input.type === 'email' && !input.value.includes('@')) {
        allFilled = false;
        break;
      }
    }

    btn.disabled = !allFilled;
  }

  // ── Form submit guard ──

  #handleFormSubmit(e) {
    const qty = this.#getQuantity();
    const missing = [];

    for (let i = 2; i <= qty; i++) {
      const name = this.querySelector(`[name="properties[_guest_${i}_name]"]`);
      const email = this.querySelector(`[name="properties[_guest_${i}_email]"]`);
      if (!name?.value?.trim() || !email?.value?.trim()) {
        missing.push(i);
      }
    }

    if (missing.length > 0) {
      e.preventDefault();
      const label = missing.length === 1
        ? `Ticket ${missing[0]} is missing guest details.`
        : `Tickets ${missing.join(', ')} are missing guest details.`;
      alert(`${label} Please fill in name and email for every guest.`);

      // Focus the first empty field
      const firstIdx = missing[0];
      const firstEmpty = this.querySelector(`[name="properties[_guest_${firstIdx}_name]"]`);
      firstEmpty?.focus();
      return;
    }

    // Stamp a unique line id so re-adds never merge with an existing cart line
    if (this.refs.lineId) {
      this.refs.lineId.value = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    }
  }

  // ── Price display ──

  #syncOrderPricing() {
    const qty = this.#getQuantity();
    const btn = this.refs.addToCartBtn;

    if (this.refs.summaryQty) {
      this.refs.summaryQty.textContent = String(qty);
    }
    if (this.refs.summaryPlural) {
      this.refs.summaryPlural.textContent = qty === 1 ? '' : 's';
    }

    if (!this.#unitPrice) {
      if (this.refs.summaryTotal) this.refs.summaryTotal.textContent = this.#formatMoney(0);
      if (btn) btn.textContent = 'Add to Cart';
      return;
    }

    const total = this.#unitPrice * qty;
    const formatted = this.#formatMoney(total);
    if (this.refs.summaryTotal) {
      this.refs.summaryTotal.textContent = formatted;
    }
    if (btn) {
      btn.textContent = `Add to Cart — ${formatted}`;
    }
  }

  // ── Persist buyer details across page loads ──

  #saveBuyerDetails() {
    try {
      const data = {};
      for (const id of SwfTicketFormComponent.#BUYER_FIELDS) {
        const input = this.querySelector(`#${id}`);
        if (input) data[id] = input.value;
      }
      localStorage.setItem(SwfTicketFormComponent.#STORAGE_KEY, JSON.stringify(data));
    } catch { /* storage unavailable */ }
  }

  #restoreBuyerDetails() {
    try {
      const raw = localStorage.getItem(SwfTicketFormComponent.#STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      for (const id of SwfTicketFormComponent.#BUYER_FIELDS) {
        const input = this.querySelector(`#${id}`);
        if (input && data[id] && !input.value) {
          input.value = data[id];
        }
      }
    } catch { /* storage unavailable or corrupt */ }
  }

  #formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }
}

if (!customElements.get('swf-ticket-form-component')) {
  customElements.define('swf-ticket-form-component', SwfTicketFormComponent);
}
